import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Check, X, Loader2, Crown } from "lucide-react";

// One row per checkout kind → keeps JSX free of nested ternaries.
const KIND_COPY = {
  tour_pass: {
    overline: "Tour Pass activated",
    headline: "Welcome, VIP.",
    sub: "Your VIP badge is live. Every drop now opens 12 hours early for you.",
    ctaLabel: "Open Tour Pass",
    ctaHref: "/tour-pass",
    tone: "gold",
  },
  donation: {
    overline: "Fuel received",
    headline: "You're a mile.",
    sub: "Your name (or 'Anonymous') is now on the Fuel wall. Thank you, seriously.",
    ctaLabel: "See the wall",
    ctaHref: "/fuel",
    tone: "gold",
  },
  sponsor_city: {
    overline: "Sponsor slot locked",
    headline: "City booked.",
    sub: "Our team will reach out within 24h to confirm creative + activation date.",
    ctaLabel: "Back to press",
    ctaHref: "/press",
    tone: "gold",
  },
  default: {
    overline: "Order confirmed",
    headline: "You're in.",
    sub: "We'll ship from the next stop. Confirmation hits your inbox shortly.",
    ctaLabel: "Back to shop",
    ctaHref: "/shop",
    tone: "pink",
  },
};

function copyFor(kind) {
  return KIND_COPY[kind] || KIND_COPY.default;
}

export default function SuccessPage() {
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", data: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", data: null });
      return undefined;
    }
    let attempts = 0;
    let cancelled = false;
    let timer;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid", data });
          if (data.kind === "tour_pass") await refresh?.();
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", data });
          return;
        }
        if (attempts >= 8) {
          setState({ status: "timeout", data });
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch (err) {
        if (cancelled) return;
        console.warn("checkout status poll failed:", err?.response?.status || err?.message);
        if (attempts >= 4) {
          setState({ status: "error", data: null });
        } else {
          timer = setTimeout(poll, 2000);
        }
      }
    };
    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [sessionId, refresh]);

  const kind = state.data?.kind;
  const c = copyFor(kind);
  const isTourPass = kind === "tour_pass";

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="success-page">
      {state.status === "polling" && (
        <>
          <Loader2 size={48} className="text-[#FF1493] animate-spin mx-auto" />
          <h1 className="font-display text-5xl uppercase mt-6">Confirming...</h1>
          <p className="text-white/70 mt-3">Hold tight, talking to Stripe.</p>
        </>
      )}
      {state.status === "paid" && (
        <>
          <div
            className={`w-16 h-16 mx-auto flex items-center justify-center ${
              c.tone === "gold" ? "bg-[#FFC107] text-[#0A0A0A]" : "bg-[#FF1493]"
            }`}
          >
            {isTourPass ? <Crown size={32} fill="#0A0A0A" /> : <Check size={32} />}
          </div>
          <div className="overline mt-6">{c.overline}</div>
          <h1 className="font-display text-5xl uppercase mt-3 drip-shadow" data-testid="success-paid">
            {c.headline}
          </h1>
          <p className="text-white/70 mt-3">
            <span className="text-white">{state.data?.product_name}</span> · $
            {((state.data?.amount_total ?? 0) / 100).toFixed(2)}
          </p>
          <p className="text-white/60 text-sm mt-2">{c.sub}</p>
          <Link
            to={c.ctaHref}
            className="btn-blaze mt-8 inline-flex"
            data-testid="success-back"
          >
            {c.ctaLabel}
          </Link>
        </>
      )}
      {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
        <>
          <div className="w-16 h-16 mx-auto bg-red-500/20 border border-red-500 flex items-center justify-center">
            <X size={32} className="text-red-400" />
          </div>
          <h1 className="font-display text-5xl uppercase mt-6">Something went sideways</h1>
          <p className="text-white/70 mt-3">
            Payment didn't complete. No charge made — try again from the shop.
          </p>
          <Link to="/shop" className="btn-blaze mt-8 inline-flex" data-testid="success-retry">
            Back to shop
          </Link>
        </>
      )}
    </div>
  );
}
